import React, { useEffect, useState } from "react";
import {
  Typography,
  Form,
  Input,
  Button,
  Divider,
  Card,
  notification,
  message
} from "antd";
import Logo from "assets/new-hire-logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RoleTypes } from "enum/role-type-enum";
import { useLoginEnumerator } from "api/enumerator/Login/useEnumeratorLogin";
import { useLoginRecruiter } from "api/recruiter/Login/useRecruiterLogin";
import GoogleIconSvg from "assets/devicon_google.svg";
// import FacebookIconSvg from "assets/logos_facebook.svg";
// import { useFacebookAuth } from "api/register/useSignUpWithFacebook";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth, db } from "../../../firebase/firebase.js";
import { query, where, collection, getDocs } from "firebase/firestore";
import { storage } from "utils/storage";
import { userCreateInterface } from "api/register/dto";

const { Title, Text } = Typography;

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Expected values: "enumerator" or "recruiter"
  const queryValue = queryParams.get("roleType") || "";

  const loginEnumeratorMutation = useLoginEnumerator();
  const loginRecruiterMutation = useLoginRecruiter();
  // const facebookAuthMutation = useFacebookAuth(false); // false indicates login
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    const storedUser = storage.getUserFromStorage();
    if (storedUser && location.pathname === "/login") {
      if (
        storedUser.role &&
        storedUser.role.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()
      ) {
        navigate("/recruiter/home");
      } else if (
        storedUser.role &&
        storedUser.role.toLowerCase() === RoleTypes.ENUMERATOR.toLowerCase()
      ) {
        navigate("/enumerator/home");
      }
    }
  }, [navigate, location]);

  const handleSubmit = async () => {
    try {
      const parameters = await form.validateFields();
      if (!queryValue) return;

      const payload = {
        emailOrPhone: parameters.email,
        role: queryValue,
        password: parameters.password,
      };

      setIsLoading(true);


      if (queryValue.toLowerCase() === RoleTypes.ENUMERATOR.toLowerCase()) {
        loginEnumeratorMutation.mutate(payload, {

          onSettled: () => setIsLoading(false),
        });
      } else if (
        queryValue.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()
      ) {
        loginRecruiterMutation.mutate(payload, {

          onSettled: () => setIsLoading(false),
        });
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (!queryValue) {
      notification.error({
        message: "Missing Role",
        description: "Role type is missing from the URL.",
      });
      return;
    }

    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user = result.user;

      if (user.providerData[0].providerId !== "google.com") {
        message.error("Your Email is registered, but not with Google Account please retry with other method")
        return;
      }

      if (!user.email) {
        throw new Error("User email is missing from Google account.");
      }

      // Fetch user document from Firestore to verify role
      const userInfosCollection = collection(db, "user_infos");
      const queryForUserInfo = query(userInfosCollection, where("uid", "==", user.uid));
      const querySnapshot = await getDocs(queryForUserInfo);

      if (querySnapshot.empty) {
        throw new Error("User Not Registered");
      }

      const userDocSnap = querySnapshot.docs[0];
      const existingUserInfo = userDocSnap.data() as userCreateInterface;

      // Check that the stored role matches the login method.
      if (
        !existingUserInfo.role ||
        existingUserInfo.role.toLowerCase() !== queryValue.toLowerCase()
      ) {
        notification.error({
          message: "Login Error",
          description: `Your registered role (${existingUserInfo.role}) does not match the chosen login method.`,
        });
        return;
      }

      const idToken = await user.getIdToken();

      // Construct user info object based on Google account
      const userInfo: userCreateInterface = {
        email: user.email,
        role: existingUserInfo.role, // from Firestore
        uid: user.uid,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ")[1] || "",
        createdAt: existingUserInfo.createdAt,
        isVerified: existingUserInfo.isVerified,
      };

      storage.setUserInStorage(userInfo);
      storage.setTokenInStorage(idToken);

      if (queryValue.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()) {
        navigate("/recruiter/home");
      } else if (
        queryValue.toLowerCase() === RoleTypes.ENUMERATOR.toLowerCase()
      ) {
        navigate("/enumerator/home");
      }
      message.success("You have successfully logged in.");
    } catch (error) {
      notification.error({
        message: "Google Sign-In Failed",
        description:
          error instanceof Error ? error.message : "An error occurred.",
      });
    }
  };

  // const handleFacebookAuth = async () => {
  //   facebookAuthMutation.mutate(navigate);
  // };

  return (
    <div className="h-screen bg-background overflow-auto px-5 md:px-0">
      <div className="max-w-lg mx-auto my-6">
        <div className="flex justify-center mb-0">
          <img alt="logo" src={Logo} width={350} />
        </div>
        <Card className="px-4 py-6">
          <Title level={2}>Account sign in</Title>
          <Text type="secondary">
            Welcome to the login page. You can either login or register a new
            account if you don't already have one.
          </Text>
          <div className="mt-12">
            <Form layout="vertical" form={form} onFinish={handleSubmit}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, message: "Email is required" }]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password placeholder="Enter password here" />
              </Form.Item>
              <Button
                loading={isLoading}
                htmlType="submit"
                type="primary"
                className="h-12 px-8 border-2 border-border text-base flex justify-center items-center gap-2 bg-primary text-white border-none w-full mt-12"
              >
                Login
              </Button>
            </Form>
            <Divider>Or</Divider>
            <div className="flex flex-col gap-4">
              <Button
                className="h-12 px-8 border-1 border-grey text-base font-bold flex justify-center items-center gap-2 w-full"
                onClick={handleGoogleAuth}
              >
                <img alt="Google Icon" src={GoogleIconSvg} />
                Continue with Google
              </Button>
              {/* <Button
                className="h-12 px-8 border-1 border-grey text-base font-bold flex justify-center items-center gap-2 w-full"
                onClick={handleFacebookAuth}
              >
                <img alt="Facebook Icon" src={FacebookIconSvg} />
                Continue with Facebook
              </Button> */}
            </div>
            <div className="mt-8">
              <Text className="text-base">
                Don't remember your password?{" "}
                <Link to={`/forgot-password?roleType=${queryValue}`}>
                  Click here.
                </Link>
              </Text>
            </div>
            <div className="mt-8">
              <Button
                type="default"
                onClick={() => navigate(-1)}
                className="h-12 px-8 border-2 border-border text-base flex justify-center items-center gap-2 w-full"
              >
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;